import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import Load from '../../../components/Load';
import TournamentSponsorItem from '../../admin/sponser/components/TournamentSponsorItem';
import {
  PATROCINADORES_POR_TORNEIO,
  PatrocinadoresPorTorneioData,
  PatrocinadoresPorTorneioVars,
  criarPatrocinadorTorneio,
  atualizarPatrocinadorTorneio,
  removerPatrocinadorTorneio,
  uploadFotoPatrocinador,
  PatrocinadorTorneio,
} from '../../../services/patrocinadorTorneioService';

interface TournamentSponsorsTabProps {
  torneioId: string;
  canEditLiga: boolean;
  onNotify?: (message: string, severity: 'success' | 'error') => void;
}

interface SponsorItem extends Omit<PatrocinadorTorneio, 'id'> {
  id?: string;
  tempId: string;
  file?: File;
}

const TournamentSponsorsTab: React.FC<TournamentSponsorsTabProps> = ({
  torneioId,
  canEditLiga,
  onNotify,
}) => {
  const {
    data: patrocinadoresData,
    loading: loadingPatrocinadores,
    refetch,
  } = useQuery<PatrocinadoresPorTorneioData, PatrocinadoresPorTorneioVars>(
    PATROCINADORES_POR_TORNEIO,
    {
      variables: { torneioId },
      skip: !torneioId,
    },
  );

  const [items, setItems] = useState<SponsorItem[]>([]);
  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    item: SponsorItem | null;
  }>({ open: false, item: null });

  useEffect(() => {
    if (patrocinadoresData?.patrocinadoresPorTorneio) {
      const mapped = patrocinadoresData.patrocinadoresPorTorneio.map((p) => ({
        ...p,
        tempId: p.id,
      }));
      setItems(mapped);
    }
  }, [patrocinadoresData]);

  const handleAddClick = () => {
    const tempId = `temp-${Date.now()}`;
    const newItem: SponsorItem = {
      tempId,
      id: undefined,
      nome: '',
      torneioId,
      link: '',
      descricao: '',
      fotoUrl: null,
    };
    setItems((prev) => [newItem, ...prev]);
    setDirtyMap((prev) => ({ ...prev, [tempId]: true }));
  };

  const handleItemChange = (
    tempId: string,
    patch: Partial<PatrocinadorTorneio>,
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, ...patch } : item,
      ),
    );
    setDirtyMap((prev) => ({ ...prev, [tempId]: true }));
  };

  const handleUploadPhoto = async (tempId: string, file: File) => {
    const item = items.find((i) => i.tempId === tempId);
    if (!item) return '';

    // Se o item já tem ID, fazer upload imediato
    if (item.id) {
      setSaving((prev) => ({ ...prev, [tempId]: true }));
      try {
        const result = await uploadFotoPatrocinador(item.id, file);
        await refetch();
        return result.fotoUrl;
      } catch (e: any) {
        const errorMessage =
          e?.message ||
          e?.response?.data?.message ||
          'Erro ao fazer upload da foto';
        if (onNotify) {
          onNotify(errorMessage, 'error');
        }
        throw e;
      } finally {
        setSaving((prev) => {
          const { [tempId]: _removed, ...rest } = prev;
          return rest;
        });
      }
    } else {
      // Para novos itens, guardar o arquivo para upload posterior
      setItems((prev) =>
        prev.map((i) => (i.tempId === tempId ? { ...i, file } : i)),
      );
      return URL.createObjectURL(file);
    }
  };

  const handleSaveItem = async (tempId: string) => {
    const item = items.find((i) => i.tempId === tempId);
    if (!item) return;

    if (!item.nome?.trim()) {
      const message = 'Nome do patrocinador é obrigatório';
      if (onNotify) {
        onNotify(message, 'error');
      }
      return;
    }

    setSaving((prev) => ({ ...prev, [tempId]: true }));
    try {
      let savedItem: PatrocinadorTorneio;

      if (item.id) {
        // Atualizar existente
        savedItem = (await atualizarPatrocinadorTorneio({
          id: item.id,
          input: {
            torneioId,
            nome: item.nome,
            link: item.link || null,
            descricao: item.descricao || null,
            fotoUrl: item.fotoUrl || null,
          },
        }))!;

        // Se há arquivo para upload, fazer upload agora
        if (item.file && savedItem?.id) {
          await uploadFotoPatrocinador(savedItem.id, item.file);
        }
      } else {
        // Criar novo: se houver arquivo de foto, criar com fotoUrl vazia primeiro
        const fileToUpload = item.file;
        const hasFile = !!fileToUpload;
        savedItem = (await criarPatrocinadorTorneio({
          torneioId,
          nome: item.nome,
          link: item.link || null,
          descricao: item.descricao || null,
          fotoUrl: hasFile ? null : item.fotoUrl || null,
        }))!;

        // Se há arquivo para upload, fazer upload agora com o id do registro criado
        if (hasFile && savedItem?.id && fileToUpload) {
          await uploadFotoPatrocinador(savedItem.id, fileToUpload);
        }
      }

      await refetch();

      const message = item.id
        ? 'Patrocinador atualizado com sucesso'
        : 'Patrocinador adicionado com sucesso';
      if (onNotify) {
        onNotify(message, 'success');
      }
    } catch (e: any) {
      const errorMessage =
        e?.message ||
        e?.response?.data?.message ||
        (item.id
          ? 'Erro ao atualizar patrocinador'
          : 'Erro ao adicionar patrocinador');
      if (onNotify) {
        onNotify(errorMessage, 'error');
      }
    } finally {
      setSaving((prev) => {
        const { [tempId]: _removed, ...rest } = prev;
        return rest;
      });
      setDirtyMap((prev) => {
        const { [tempId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleDelete = (item: SponsorItem) => {
    setDeleteConfirmDialog({ open: true, item });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({ open: false, item: null });
  };

  const handleConfirmDelete = async () => {
    const item = deleteConfirmDialog.item;
    if (!item || !item.id) {
      handleCloseDeleteDialog();
      return;
    }

    setDeletingMap((prev) => ({ ...prev, [item.tempId]: true }));
    try {
      await removerPatrocinadorTorneio(item.id);
      await refetch();
      const message = 'Patrocinador removido com sucesso';
      if (onNotify) {
        onNotify(message, 'success');
      }
      handleCloseDeleteDialog();
    } catch (e: any) {
      const errorMessage =
        e?.message ||
        e?.response?.data?.message ||
        'Erro ao remover patrocinador';
      if (onNotify) {
        onNotify(errorMessage, 'error');
      }
    } finally {
      setDeletingMap((prev) => {
        const { [item.tempId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  if (loadingPatrocinadores) {
    return <Load />;
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-secondary"
          onClick={handleAddClick}
          disabled={!canEditLiga}
        >
          Novo patrocinador +
        </button>
      </div>

      <div className="row g-3">
        {items.map((item) => {
          const isDirty = dirtyMap[item.tempId];
          const isSaving = saving[item.tempId];
          const isDeleting = deletingMap[item.tempId];
          const isNew = !item.id;
          const busy = isSaving || isDeleting;

          return (
            <div key={item.tempId} className="col-12">
              <TournamentSponsorItem
                value={item}
                onChange={(patch) => handleItemChange(item.tempId, patch)}
                onUploadPhoto={(file) => handleUploadPhoto(item.tempId, file)}
                onDelete={
                  canEditLiga && item.id
                    ? () => handleDelete(item)
                    : undefined
                }
                busy={busy}
                readOnly={!canEditLiga}
              />
              {canEditLiga && (isDirty || isNew) && (
                <div className="d-flex justify-content-end gap-2 mt-2">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleSaveItem(item.tempId)}
                    disabled={busy}
                  >
                    {isSaving
                      ? 'Salvando...'
                      : isNew
                        ? 'Adicionar'
                        : 'Salvar'}
                  </button>
                  {isNew && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setItems((prev) =>
                          prev.filter((i) => i.tempId !== item.tempId),
                        );
                        setDirtyMap((prev) => {
                          const { [item.tempId]: _removed, ...rest } = prev;
                          return rest;
                        });
                      }}
                      disabled={busy}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialog de Confirmação de Deleção */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={
          deletingMap[deleteConfirmDialog.item?.tempId || '']
            ? undefined
            : handleCloseDeleteDialog
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remover Patrocinador</DialogTitle>
        <DialogContent dividers>
          <p>
            Tem certeza que deseja remover o patrocinador{' '}
            <strong>
              {deleteConfirmDialog.item?.nome || 'este patrocinador'}
            </strong>
            ? Esta ação não pode ser desfeita.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={!!deletingMap[deleteConfirmDialog.item?.tempId || '']}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={!!deletingMap[deleteConfirmDialog.item?.tempId || '']}
          >
            {deletingMap[deleteConfirmDialog.item?.tempId || '']
              ? 'Removendo...'
              : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TournamentSponsorsTab;

